/**
 * Editorial categorisation for `artist_media`.
 *
 * Vocabulary is enforced at the DB layer by the
 * `artist_media_category_check` CHECK constraint (see migration
 * `20260516120000_extend_artist_media_category_for_spec_taxonomy.sql`).
 * Document rows may additionally carry the `DocumentCategory` values; the
 * constraint pair-validates them to `media_type = 'document'`.
 * Legacy Meteor values (`header`, `arabesque`, `ballet_class`,
 * `classical_variation`, `modern_variation`, `resume`) also remain valid
 * in the DB but are NOT exposed in editors — they render in the
 * "Other" bucket until manually re-categorised.
 *
 * Source spec: docs/features/dancer-profile/audit-2026-05-16.md §3.
 *
 * Section order is intentional, not alphabetical:
 *   - Photos lead with Audition (proof of casting eligibility).
 *   - Videos lead with Performance (highest-value casting signal).
 * Both lists match Philippine's 2026-05-12 proof-first reorder.
 */

import { z } from 'zod';

export type PhotoCategory = 'audition' | 'stage_photo' | 'brand_editorial';

export type VideoCategory =
  | 'audition'
  | 'performance_video'
  | 'improvisation'
  | 'commercial'
  | 'choreography';

export type MediaCategory = PhotoCategory | VideoCategory;

/**
 * Document-only vocabulary. Valid only on rows with `media_type =
 * 'document'` (migration `20261024143000_artist_media_document_categories.sql`).
 * Documents are not bucketed by the photo/video galleries, so this is not part
 * of `MediaCategory`.
 */
export type DocumentCategory =
  | 'cv'
  | 'certificate'
  | 'recommendation_letter'
  | 'id_document'
  | 'financial'
  | 'other';

export interface MediaCategoryOption<T extends string = MediaCategory> {
  value: T;
  label: string;
  description: string;
}

export const PHOTO_CATEGORIES: ReadonlyArray<
  MediaCategoryOption<PhotoCategory>
> = [
  {
    value: 'audition',
    label: 'Audition / studio',
    description: 'Class, audition, studio photos',
  },
  {
    value: 'stage_photo',
    label: 'Stage',
    description: 'On-stage performance photos',
  },
  {
    value: 'brand_editorial',
    label: 'Brand / editorial',
    description: 'Editorial shoots, campaigns, brand work',
  },
];

export const VIDEO_CATEGORIES: ReadonlyArray<
  MediaCategoryOption<VideoCategory>
> = [
  {
    value: 'performance_video',
    label: 'Performance',
    description: 'Live performance footage',
  },
  {
    value: 'audition',
    label: 'Audition',
    description: 'Audition / class videos',
  },
  {
    value: 'choreography',
    label: 'Choreography',
    description: 'Choreographic work you created',
  },
  {
    value: 'commercial',
    label: 'Commercial',
    description: 'Brand campaigns, commercials, music videos',
  },
  {
    value: 'improvisation',
    label: 'Improvisation',
    description: 'Improvised / freestyle pieces',
  },
];

export const DOCUMENT_CATEGORIES: ReadonlyArray<
  MediaCategoryOption<DocumentCategory>
> = [
  { value: 'cv', label: 'CV', description: 'Curriculum vitae or resume file' },
  {
    value: 'certificate',
    label: 'Certificate',
    description: 'Diplomas, training and exam certificates',
  },
  {
    value: 'recommendation_letter',
    label: 'Recommendation letter',
    description: 'Letters of recommendation',
  },
  {
    value: 'id_document',
    label: 'ID document',
    description: 'Passport, ID card, residence or work permit',
  },
  {
    value: 'financial',
    label: 'Financial',
    description: 'Invoices, tax and bank documents',
  },
  { value: 'other', label: 'Other', description: 'Any other document' },
];

export const PHOTO_CATEGORY_VALUES: ReadonlyArray<PhotoCategory> =
  PHOTO_CATEGORIES.map((c) => c.value);

export const VIDEO_CATEGORY_VALUES: ReadonlyArray<VideoCategory> =
  VIDEO_CATEGORIES.map((c) => c.value);

export const DOCUMENT_CATEGORY_VALUES: ReadonlyArray<DocumentCategory> =
  DOCUMENT_CATEGORIES.map((c) => c.value);

/**
 * Spec-only vocabulary (no legacy values). Single source of truth for
 * any server-action Zod schema that accepts a category. Pair-validation
 * (photo categories on video rows, etc.) happens in the DB via
 * `artist_media_category_check`.
 */
export const DancerMediaCategorySchema = z.enum([
  'audition',
  'stage_photo',
  'brand_editorial',
  'performance_video',
  'improvisation',
  'commercial',
  'choreography',
  'cv',
  'certificate',
  'recommendation_letter',
  'id_document',
  'financial',
  'other',
]);

/**
 * Nullable variant for editor surfaces that allow "clear category" via
 * a NULL passthrough. Optional at the outer schema level — omit to
 * leave the column untouched.
 */
export const DancerMediaCategoryNullableSchema = z.union([
  DancerMediaCategorySchema,
  z.null(),
]);

/**
 * Legacy values still permitted by the CHECK constraint. Used to detect
 * "non-spec" categorised rows and route them to the Other bucket in
 * the public render rather than displaying the raw legacy label.
 */
export const LEGACY_CATEGORY_VALUES: ReadonlyArray<string> = [
  'header',
  'arabesque',
  'ballet_class',
  'classical_variation',
  'modern_variation',
  'resume',
];

export function isPhotoCategory(value: unknown): value is PhotoCategory {
  return (
    typeof value === 'string' &&
    (PHOTO_CATEGORY_VALUES as ReadonlyArray<string>).includes(value)
  );
}

export function isVideoCategory(value: unknown): value is VideoCategory {
  return (
    typeof value === 'string' &&
    (VIDEO_CATEGORY_VALUES as ReadonlyArray<string>).includes(value)
  );
}

export function isDocumentCategory(value: unknown): value is DocumentCategory {
  return (
    typeof value === 'string' &&
    (DOCUMENT_CATEGORY_VALUES as ReadonlyArray<string>).includes(value)
  );
}

export function isLegacyCategory(value: unknown): boolean {
  return typeof value === 'string' && LEGACY_CATEGORY_VALUES.includes(value);
}

export function getPhotoCategoryLabel(value: PhotoCategory): string {
  return PHOTO_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

export function getVideoCategoryLabel(value: VideoCategory): string {
  return VIDEO_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

/**
 * Bucket a list of media items by their category. Items whose category
 * is NULL, unknown, or a legacy Meteor value fall into the `null`
 * bucket (rendered as "Other" in the UI).
 */
export function bucketByCategory<
  T extends { category?: string | null },
  C extends MediaCategory,
>(items: ReadonlyArray<T>, categories: ReadonlyArray<C>): Map<C | null, T[]> {
  const buckets = new Map<C | null, T[]>();
  for (const cat of categories) buckets.set(cat, []);
  buckets.set(null, []);

  const validSet = new Set<string>(categories);
  for (const item of items) {
    const cat = item.category ?? null;
    if (cat && validSet.has(cat)) {
      buckets.get(cat as C)!.push(item);
    } else {
      buckets.get(null)!.push(item);
    }
  }
  return buckets;
}
